import { useSnackbar } from 'notistack';
import { useCallback } from 'react';

export function useSuccessFailureNotification() {
  const { enqueueSnackbar } = useSnackbar();

  const callWithNotification = useCallback(
    async <
      T extends boolean | { success?: boolean | null; error?: string | null } | null | undefined,
    >(
      callback: () => Promise<T>,
      successMessage: string,
      failureMessage = 'Sorry, something went wrong. Please try again later.',
      onSuccess?: () => void,
    ) => {
      try {
        const result = await callback();

        if (result == null) {
          enqueueSnackbar(failureMessage, { variant: 'error' });
          return;
        }

        if (typeof result === 'boolean') {
          if (!result) {
            throw new Error('Failed to update');
          }
          enqueueSnackbar(successMessage, { variant: 'success' });
          onSuccess?.();
          return;
        }

        const error = result.error ?? (!result.success ? failureMessage : null);
        if (error) {
          enqueueSnackbar(error, { variant: 'error' });
          return;
        }

        enqueueSnackbar(successMessage, { variant: 'success' });
        onSuccess?.();
      } catch {
        enqueueSnackbar(failureMessage, { variant: 'error' });
      }
    },
    [enqueueSnackbar],
  );

  return { callWithNotification };
}
